import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxFacilityDto, type DxFacilityKind } from "@/api/diagnostics";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/facilities";
const LIST_KEY = ["dx-facilities"];
const KINDS: DxFacilityKind[] = ["Lab", "Radiology", "Collection", "Other"];

export function DiagnosticsFacilitiesPage() {
  const rooms = useQuery({ queryKey: ["dx-rooms"], queryFn: () => diagnosticsApi.listRooms() });
  const roomName = new Map((rooms.data ?? []).map(r => [r.id, `${r.code} — ${r.name}`]));

  return (
    <MasterListPage<DxFacilityDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Facilities"
      subtitle="Labs, radiology suites and collection points that orders route to."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listFacilities(undefined, inc)}
      deleteFn={diagnosticsApi.deleteFacility}
      basePath={BASE}
      newLabel="New facility"
      labelOf={(r) => r.name}
      deleteNote="It will be refused while it still has machines or any order has been routed to it."
      searchPlaceholder="Search by code, name, kind, or room"
      searchText={(r) => `${r.code} ${r.name} ${r.kind} ${(r.roomId && roomName.get(r.roomId)) || ""}`}
      columns={[
        { field: "code", headerName: "Code", width: 130 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
        { field: "kind", headerName: "Kind", width: 120 },
        {
          field: "roomId", headerName: "Room", width: 180,
          renderCell: (p) => (p.row.roomId && roomName.get(p.row.roomId)) || "—",
        },
        { field: "capacity", headerName: "Capacity", width: 100, align: "right", headerAlign: "right" },
        statusColumn<DxFacilityDto>(),
      ]}
    />
  );
}

interface Values {
  code: string; name: string; kind: DxFacilityKind; roomId: string;
  departmentId: string; capacity: string; isActive: boolean;
}

export function FacilityFormPage() {
  const rooms = useQuery({ queryKey: ["dx-rooms"], queryFn: () => diagnosticsApi.listRooms() });
  const departments = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });

  return (
    <MasterFormPage<Values, DxFacilityDto>
      breadcrumbs={["Diagnostics", "Facilities"]}
      noun="facility"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-facility", id]}
      defaultValues={{ code: "", name: "", kind: "Lab", roomId: "", departmentId: "", capacity: "1", isActive: true }}
      getFn={diagnosticsApi.getFacility}
      toValues={(d) => ({
        code: d.code, name: d.name, kind: d.kind as DxFacilityKind,
        roomId: d.roomId ?? "", departmentId: d.departmentId ?? "",
        capacity: String(d.capacity), isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createFacility({
        code: v.code.trim(), name: v.name.trim(), kind: v.kind,
        roomId: v.roomId || null, departmentId: v.departmentId || null, capacity: Number(v.capacity),
      })}
      updateFn={(id, v) => diagnosticsApi.updateFacility(id, {
        code: v.code.trim(), name: v.name.trim(), kind: v.kind,
        roomId: v.roomId || null, departmentId: v.departmentId || null,
        capacity: Number(v.capacity), isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />
          <SelectCell control={control} name="kind" label="Kind" span={4} required
            options={KINDS.map(k => ({ value: k, label: k }))} emptyLabel="— select kind —" />
          <TextFieldCell control={control} name="capacity" label="Capacity" span={4} required type="number"
            helperText="Concurrent orders before the facility reads as crowded." />
          <SelectCell control={control} name="roomId" label="Room" span={4}
            options={(rooms.data ?? []).map(r => ({ value: r.id, label: `${r.code} — ${r.name}` }))} />
          <SelectCell control={control} name="departmentId" label="Department" span={12}
            options={(departments.data ?? []).map(d => ({ value: d.id, label: `${d.code} — ${d.name}` }))} />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
